/**
 * SlugEditor — inline "Change" button + edit modal for a dealer's /slug URL.
 * Handles validation feedback, uniqueness collisions, and reserved-word errors
 * returned from the backend. Persists via PUT /api/dealer/slug.
 *
 * The old slug keeps working forever (backend records it in slug_history and
 * the LandingPage does a silent redirect) so business cards & social posts
 * printed with the old URL never break.
 */
import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, Loader2, X, Check, ExternalLink } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Kept in sync with backend RESERVED_SLUGS. Shown to the user as a friendlier
// pre-validation hint so they don't have to submit to learn the word is taken.
const CLIENT_HINT_RESERVED = new Set([
  'quiz', 'sign-in', 'signin', 'login', 'favorites', 'admin', 'dashboard',
  'settings', 'help', 'how-to', 'onboarding', 'matches', 'my-matches',
  'ranger', 'chat', 'api', 'app', 'dealer', 'd', 'home', 'about', 'contact',
]);

const normalize = (raw) =>
  (raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const validate = (slug) => {
  if (!slug) return 'Enter a slug.';
  if (slug.length < 3) return 'Too short — at least 3 characters.';
  if (slug.length > 40) return 'Too long — 40 characters max.';
  if (CLIENT_HINT_RESERVED.has(slug)) return `"${slug}" is reserved. Try a different word.`;
  return null;
};

const SlugEditor = ({ currentSlug, token, isDark, textPrimary, textSecondary, cardBg, border, accent, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState(currentSlug || '');
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);

  const normalized = useMemo(() => normalize(raw), [raw]);
  const localError = useMemo(() => validate(normalized), [normalized]);
  const isUnchanged = normalized === (currentSlug || '');
  const previewUrl = normalized ? `${window.location.origin}/${normalized}` : '';

  const startEditing = () => {
    setRaw(currentSlug || '');
    setServerError(null);
    setOpen(true);
  };

  const submit = async () => {
    if (localError || isUnchanged) return;
    setSaving(true);
    setServerError(null);
    try {
      const res = await axios.put(
        `${API}/dealer/slug`,
        { slug: normalized },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.success) {
        toast.success(
          res.data.previous_slug
            ? `Slug updated. /${res.data.previous_slug} still works and redirects here.`
            : 'Slug updated.',
        );
        setOpen(false);
        onChanged?.(res.data.slug);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not update slug. Try again.';
      setServerError(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={startEditing}
        data-testid="slug-edit-btn"
        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-md transition-colors ${
          isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
        }`}
        aria-label="Change your public link slug"
      >
        <Pencil size={12} /> Change
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[95] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="slug-edit-title"
          data-testid="slug-edit-modal"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => !saving && setOpen(false)}
            data-testid="slug-edit-backdrop"
          />
          <div
            className="relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
            style={{ background: cardBg, border: `1px solid ${border}` }}
          >
            <div className="px-6 pt-5 pb-3 flex items-start gap-3">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${accent}20`, color: accent }}
              >
                <ExternalLink size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 id="slug-edit-title" className="text-lg font-bold" style={{ color: textPrimary }}>
                  Customize your public link
                </h3>
                <p className="text-sm mt-0.5" style={{ color: textSecondary }}>
                  This is the URL you share with buyers. Old links keep working — they auto-redirect.
                </p>
              </div>
              <button
                type="button"
                onClick={() => !saving && setOpen(false)}
                className="shrink-0 p-1 rounded-md hover:bg-white/10"
                style={{ color: textSecondary }}
                aria-label="Close"
                data-testid="slug-edit-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-6 pb-5 space-y-4">
              <div>
                <label
                  htmlFor="slug-input"
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: textSecondary }}
                >
                  Your slug
                </label>
                <div className="mt-1.5 flex items-stretch rounded-lg overflow-hidden" style={{ border: `1px solid ${border}` }}>
                  <span
                    className="flex items-center px-3 text-sm select-none"
                    style={{ background: isDark ? '#111827' : '#f3f4f6', color: textSecondary }}
                  >
                    {window.location.host}/
                  </span>
                  <Input
                    id="slug-input"
                    autoFocus
                    value={raw}
                    onChange={(e) => setRaw(e.target.value)}
                    placeholder="your-dealership"
                    maxLength={50}
                    style={{ background: 'transparent', border: 'none', color: textPrimary, borderRadius: 0 }}
                    data-testid="slug-edit-input"
                  />
                </div>

                {/* Real-time normalization preview */}
                {normalized && normalized !== raw.trim().toLowerCase() && (
                  <p className="mt-2 text-xs" style={{ color: textSecondary }}>
                    Will be saved as <strong style={{ color: textPrimary }}>{normalized}</strong> — we removed
                    spaces and special characters for URL safety.
                  </p>
                )}

                {localError && (
                  <p
                    className="mt-2 text-xs font-medium"
                    style={{ color: '#f87171' }}
                    data-testid="slug-local-error"
                  >
                    {localError}
                  </p>
                )}
                {serverError && !localError && (
                  <p
                    className="mt-2 text-xs font-medium"
                    style={{ color: '#f87171' }}
                    data-testid="slug-server-error"
                  >
                    {serverError}
                  </p>
                )}
              </div>

              {/* Preview card */}
              {normalized && !localError && (
                <div
                  className="rounded-lg p-3 text-sm flex items-start gap-2"
                  style={{ background: isDark ? '#0f172a' : '#f9fafb', border: `1px solid ${border}` }}
                  data-testid="slug-preview"
                >
                  <Check size={16} style={{ color: '#22c55e', marginTop: 2 }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs" style={{ color: textSecondary }}>Your new link will be:</div>
                    <div className="font-mono text-sm mt-0.5 break-all" style={{ color: textPrimary }}>
                      {previewUrl}
                    </div>
                  </div>
                </div>
              )}

              {/* Rules */}
              <ul className="text-xs space-y-1" style={{ color: textSecondary }}>
                <li>• 3-40 characters, lowercase letters/numbers/hyphens only</li>
                <li>• Old slug keeps redirecting so shared links & business cards still work</li>
                <li>• Common words like <em>quiz, admin, dashboard</em> are reserved</li>
              </ul>
            </div>

            <div
              className="px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 border-t"
              style={{ borderColor: border, background: isDark ? '#0b1220' : '#f9fafb' }}
            >
              <Button
                type="button"
                variant="ghost"
                disabled={saving}
                onClick={() => setOpen(false)}
                data-testid="slug-edit-cancel"
                className="w-full sm:w-auto"
                style={{ color: textSecondary }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={saving || !!localError || isUnchanged}
                onClick={submit}
                data-testid="slug-edit-save"
                className="w-full sm:w-auto"
                style={{ background: accent, color: '#fff', opacity: (saving || !!localError || isUnchanged) ? 0.6 : 1 }}
              >
                {saving ? <><Loader2 size={16} className="animate-spin mr-2" /> Saving…</> : 'Save new link'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SlugEditor;
